import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  FaUser, FaEnvelope, FaPhone, FaLock, FaMapMarkerAlt, FaCity, FaEye, FaEyeSlash, FaCheck, FaArrowLeft
} from 'react-icons/fa';
import { createUserWithEmailAndPassword } from "firebase/auth";
import { ref, set } from "firebase/database";
import { auth, db } from '../firebase';
import Logo from '../asstes/logo.png';

const Register = () => {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    mobile: '',
    address: '',
    city: '',
    pincode: '',
    password: '',
    confirmPassword: '',
    termsAccepted: false
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    // Apply input restrictions based on field type
    let processedValue = value;
    
    if (name === 'fullName' || name === 'city') {
      // Only allow letters and spaces
      processedValue = value.replace(/[^A-Za-z\s]/g, '');
    } else if (name === 'mobile') {
      // Only allow numbers, max 10 digits
      processedValue = value.replace(/\D/g, '').slice(0, 10);
    } else if (name === 'pincode') {
      // Only allow numbers, max 6 digits
      processedValue = value.replace(/\D/g, '').slice(0, 6);
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : processedValue
    }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Full Name - Only letters and spaces, min 3 characters
    const nameRegex = /^[A-Za-z\s]+$/;
    if (!formData.fullName.trim()) {
      newErrors.fullName = 'Full name is required';
    } else if (formData.fullName.length < 3) {
      newErrors.fullName = 'Name must be at least 3 characters';
    } else if (!nameRegex.test(formData.fullName)) {
      newErrors.fullName = 'Full name can only contain letters and spaces';
    }

    // Email - Proper email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!emailRegex.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    // Mobile - Only 10 digits, starting with 6-9
    const mobileRegex = /^[6-9]\d{9}$/;
    if (!formData.mobile) {
      newErrors.mobile = 'Mobile number is required';
    } else if (!mobileRegex.test(formData.mobile)) {
      newErrors.mobile = 'Mobile number must be 10 digits starting with 6-9';
    }

    // Address - Required field
    if (!formData.address.trim()) {
      newErrors.address = 'Address is required';
    }

    // City - Only letters and spaces
    const cityRegex = /^[A-Za-z\s]+$/;
    if (!formData.city.trim()) {
      newErrors.city = 'City is required';
    } else if (!cityRegex.test(formData.city)) {
      newErrors.city = 'City can only contain letters and spaces';
    }

    // Pincode - Exactly 6 digits
    const pincodeRegex = /^\d{6}$/;
    if (!formData.pincode) {
      newErrors.pincode = 'Pincode is required';
    } else if (!pincodeRegex.test(formData.pincode)) {
      newErrors.pincode = 'Pincode must be exactly 6 digits';
    }

    // Password validation
    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (!passwordRegex.test(formData.password)) {
      newErrors.password = 'Password must be 8+ chars with uppercase, lowercase, number & special char';
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    if (!formData.termsAccepted) {
      newErrors.termsAccepted = 'You must accept terms and conditions';
    }

    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setIsSubmitting(true);

    try {
      // Step 1: Create user in Firebase Auth
      const userCredential = await createUserWithEmailAndPassword(auth, formData.email, formData.password);
      const user = userCredential.user;

      // Step 2: Save user data with mobile number as key
      await set(ref(db, `Dr/users/${formData.mobile}`), {
        uid: user.uid,
        fullName: formData.fullName,
        email: formData.email,
        mobile: formData.mobile,
        password: formData.password,
        address: formData.address,
        city: formData.city,
        pincode: formData.pincode,
        createdAt: new Date().toISOString(),
        role: 'user'
      });

      console.log('User registered successfully with mobile:', formData.mobile);

      navigate('/login', {
        state: {
          message: 'Registration successful! Please login to continue.',
          email: formData.email
        }
      });
    } catch (error) {
      console.error('Registration error:', error);
      let errorMessage = 'Registration failed. Please try again.';

      if (error.code === 'auth/email-already-in-use') errorMessage = 'Email already registered.';
      else if (error.code === 'auth/weak-password') errorMessage = 'Password is too weak.';
      else if (error.code === 'auth/invalid-email') errorMessage = 'Invalid email address.';

      setErrors({ submit: errorMessage });
    } finally {
      setIsSubmitting(false);
    }
  };

  const getPasswordStrength = (password) => {
    if (!password) return { strength: 0, label: '', color: 'gray' };
    let strength = 0;
    if (password.length >= 8) strength++;
    if (/[a-z]/.test(password)) strength++;
    if (/[A-Z]/.test(password)) strength++;
    if (/\d/.test(password)) strength++;
    if (/[@$!%*?&]/.test(password)) strength++;

    const levels = [
      { label: 'Very Weak', color: 'red' },
      { label: 'Weak', color: 'orange' },
      { label: 'Fair', color: 'yellow' },
      { label: 'Good', color: 'lightgreen' },
      { label: 'Strong', color: 'green' }
    ];
    return levels[strength - 1] || { strength: 0, label: '', color: 'gray' };
  };

  const passwordStrength = getPasswordStrength(formData.password);

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-emerald-50 to-cyan-50 flex items-center justify-center p-4">
      <div className="max-w-4xl w-full bg-white rounded-3xl shadow-2xl overflow-hidden">
        <div className="grid grid-cols-1 lg:grid-cols-2">
          {/* Left Side - Form */}
          <div className="p-8 md:p-12 overflow-y-auto max-h-screen">
            <div className="mb-8">
              <Link to="/" className="inline-flex items-center text-teal-600 hover:text-teal-700 font-medium mb-6">
                <FaArrowLeft className="mr-2" /> Back to Home
              </Link>
              <h2 className="text-3xl font-bold text-gray-800 mb-2">Register as Patient</h2>
              <p className="text-gray-600">Create your account to book appointments</p>
            </div>

            {errors.submit && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl text-red-600">
                {errors.submit}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">

              {/* Full Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Full Name *</label>
                <div className="relative">
                  <FaUser className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleChange}
                    className={`pl-10 w-full p-3 border ${errors.fullName ? 'border-red-300' : 'border-gray-300'} rounded-xl focus:ring-2 focus:ring-teal-500`}
                    placeholder="Enter Your Name"
                  />
                </div>
                {errors.fullName && <p className="mt-1 text-sm text-red-600">{errors.fullName}</p>}
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Email Address *</label>
                <div className="relative">
                  <FaEnvelope className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className={`pl-10 w-full p-3 border ${errors.email ? 'border-red-300' : 'border-gray-300'} rounded-xl focus:ring-2 focus:ring-teal-500`}
                    placeholder="you@example.com"
                  />
                </div>
                {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email}</p>}
              </div>

              {/* Mobile */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Mobile Number *</label>
                <div className="flex">
                  <span className="inline-flex items-center px-3 border border-r-0 rounded-l-xl bg-gray-50 text-gray-600">+91</span>
                  <div className="relative flex-1">
                    <FaPhone className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      type="tel"
                      name="mobile"
                      value={formData.mobile}
                      onChange={handleChange}
                      maxLength="10"
                      className={`pl-10 w-full p-3 border ${errors.mobile ? 'border-red-300' : 'border-gray-300'} rounded-r-xl focus:ring-2 focus:ring-teal-500`}
                      placeholder="9876543210"
                    />
                  </div>
                </div>
                {errors.mobile && <p className="mt-1 text-sm text-red-600">{errors.mobile}</p>}
              </div>

              {/* Address */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Address *</label>
                <div className="relative">
                  <FaMapMarkerAlt className="absolute left-3 top-4 text-gray-400" />
                  <textarea
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    rows="3"
                    className={`pl-10 w-full p-3 border ${errors.address ? 'border-red-300' : 'border-gray-300'} rounded-xl focus:ring-2 focus:ring-teal-500`}
                    placeholder="123 Main Street, Apt 4B"
                  />
                </div>
                {errors.address && <p className="mt-1 text-sm text-red-600">{errors.address}</p>}
              </div>

              {/* City & Pincode */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">City *</label>
                  <div className="relative">
                    <FaCity className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      name="city"
                      value={formData.city}
                      onChange={handleChange}
                      className={`pl-10 w-full p-3 border ${errors.city ? 'border-red-300' : 'border-gray-300'} rounded-xl focus:ring-2 focus:ring-teal-500`}
                      placeholder="Enter Your City"
                    />
                  </div>
                  {errors.city && <p className="mt-1 text-sm text-red-600">{errors.city}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Pincode *</label>
                  <input
                    type="text"
                    name="pincode"
                    value={formData.pincode}
                    onChange={handleChange}
                    maxLength="6"
                    className={`w-full p-3 border ${errors.pincode ? 'border-red-300' : 'border-gray-300'} rounded-xl focus:ring-2 focus:ring-teal-500`}
                    placeholder="400001"
                  />
                  {errors.pincode && <p className="mt-1 text-sm text-red-600">{errors.pincode}</p>}
                </div>
              </div>

              {/* Password */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Password *</label>
                <div className="relative">
                  <FaLock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type={showPassword ? "text" : "password"}
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    className={`pl-10 pr-10 w-full p-3 border ${errors.password ? 'border-red-300' : 'border-gray-300'} rounded-xl focus:ring-2 focus:ring-teal-500`}
                    placeholder="••••••••"
                  />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2">
                    {showPassword ? <FaEyeSlash className="text-gray-500" /> : <FaEye className="text-gray-500" />}
                  </button>
                </div>

                {formData.password && (
                  <div className="mt-3">
                    <div className="flex justify-between mb-1">
                      <span className="text-xs text-gray-600">Password Strength:</span>
                      <span className={`text-xs font-bold ${passwordStrength.color === 'green' ? 'text-green-600' : passwordStrength.color === 'lightgreen' ? 'text-green-500' : 'text-' + passwordStrength.color + '-600'}`}>
                        {passwordStrength.label}
                      </span>
                    </div>
                    <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div className={`h-full transition-all duration-300 bg-${passwordStrength.color}-500`} style={{ width: `${(passwordStrength.strength / 5) * 100}%` }} />
                    </div>
                  </div>
                )}

                <div className="mt-3 space-y-1 text-xs text-gray-600">
                  <p className="flex items-center"><FaCheck className={`mr-2 ${formData.password.length >= 8 ? 'text-green-500' : 'text-gray-400'}`} /> At least 8 characters</p>
                  <p className="flex items-center"><FaCheck className={`mr-2 ${/[a-z]/.test(formData.password) ? 'text-green-500' : 'text-gray-400'}`} /> One lowercase</p>
                  <p className="flex items-center"><FaCheck className={`mr-2 ${/[A-Z]/.test(formData.password) ? 'text-green-500' : 'text-gray-400'}`} /> One uppercase</p>
                  <p className="flex items-center"><FaCheck className={`mr-2 ${/\d/.test(formData.password) ? 'text-green-500' : 'text-gray-400'}`} /> One number</p>
                  <p className="flex items-center"><FaCheck className={`mr-2 ${/[@$!%*?&]/.test(formData.password) ? 'text-green-500' : 'text-gray-400'}`} /> One special char</p>
                </div>
                {errors.password && <p className="mt-2 text-sm text-red-600">{errors.password}</p>}
              </div>

              {/* Confirm Password */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Confirm Password *</label>
                <div className="relative">
                  <FaLock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    className={`pl-10 pr-10 w-full p-3 border ${errors.confirmPassword ? 'border-red-300' : 'border-gray-300'} rounded-xl focus:ring-2 focus:ring-teal-500`}
                    placeholder="••••••••"
                  />
                  <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)} className="absolute right-3 top-1/2 -translate-y-1/2">
                    {showConfirmPassword ? <FaEyeSlash className="text-gray-500" /> : <FaEye className="text-gray-500" />}
                  </button>
                </div>
                {errors.confirmPassword && <p className="mt-1 text-sm text-red-600">{errors.confirmPassword}</p>}
              </div>

              {/* Terms */}
              <div className="flex items-start space-x-3">
                <input
                  id="terms"
                  name="termsAccepted"
                  type="checkbox"
                  checked={formData.termsAccepted}
                  onChange={handleChange}
                  className={`mt-1 h-4 w-4 text-teal-600 rounded border-gray-300 focus:ring-teal-500 ${errors.termsAccepted ? 'border-red-300' : ''}`}
                />
                <label htmlFor="terms" className="text-sm text-gray-700">
                  I agree to the <Link to="/terms" className="text-teal-600 hover:underline">Terms</Link> and <Link to="/privacy" className="text-teal-600 hover:underline">Privacy Policy</Link>
                </label>
              </div>
              {errors.termsAccepted && <p className="text-sm text-red-600">{errors.termsAccepted}</p>}

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isSubmitting}
                className={`w-full py-4 rounded-xl font-bold text-white flex items-center justify-center transition-all ${
                  isSubmitting
                    ? 'bg-teal-400 cursor-not-allowed'
                    : 'bg-gradient-to-r from-teal-600 to-emerald-600 hover:from-teal-700 hover:to-emerald-700 shadow-lg'
                }`}
              >
                {isSubmitting ? (
                  <> <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-3" /> Creating Account... </>
                ) : (
                  'Register Now'
                )}
              </button>
            </form>

            <div className="mt-8 text-center text-sm text-gray-600">
              Already have an account?{' '}
              <Link to="/login" className="font-medium text-teal-600 hover:text-teal-500">Sign in here</Link>
            </div>
          </div>

          {/* Right Side - Same as before */}
          <div className="hidden lg:block bg-gradient-to-br from-teal-600 to-emerald-700 p-12 relative overflow-hidden">
            {/* Your existing illustration panel */}
            <div className="absolute inset-0 opacity-5" style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
              backgroundSize: '60px 60px'
            }}></div>

            <div className="relative z-10 h-full flex flex-col justify-center text-white">
              <div className="text-center mb-8">
                <div className="inline-flex items-center justify-center w-20 h-20 bg-white/10 rounded-2xl mb-6">
                  <img src={Logo} alt="Anusaya Logo" className="w-16 h-16 object-contain" />
                </div>
                <h3 className="text-3xl font-bold mb-3">Welcome to Anusaya</h3>
                <p className="text-teal-100 text-lg">Book appointments easily and manage your health</p>
              </div>
              {/* Features and testimonial remain same */}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;